import React, { useEffect, useRef } from "react";

export const BubbleBackground = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        let animationFrameId;
        let width = (canvas.width = window.innerWidth);
        let height = (canvas.height = window.innerHeight);

        const handleResize = () => {
            if (!canvas) return;
            width = canvas.width = window.innerWidth;
            height = canvas.height = window.innerHeight;
            initBubbles();
        };

        window.addEventListener("resize", handleResize);

        // Mouse tracking with smooth lerp
        const mouse = {
            x: width / 2,
            y: height / 2,
            targetX: width / 2,
            targetY: height / 2,
            radius: 200,
        };

        const handleMouseMove = (e) => {
            mouse.targetX = e.clientX;
            mouse.targetY = e.clientY;
        };

        window.addEventListener("mousemove", handleMouseMove);

        // Bubble definitions
        let bubbles = [];

        const initBubbles = () => {
            bubbles = [];

            // 1. Large ambient background glows (deep liquid orbs)
            const ambientCount = 3;
            for (let i = 0; i < ambientCount; i++) {
                bubbles.push({
                    type: "ambient",
                    baseX: (width / (ambientCount + 1)) * (i + 1) + (Math.random() - 0.5) * 200,
                    baseY: height * 0.4 + (Math.random() - 0.5) * (height * 0.4),
                    x: 0,
                    y: 0,
                    vx: (Math.random() - 0.5) * 0.25,
                    vy: (Math.random() - 0.5) * 0.25,
                    radius: Math.min(width, height) * (0.35 + Math.random() * 0.15),
                    colorInner: "rgba(255, 140, 26, 0.45)",
                    colorMid: "rgba(234, 109, 17, 0.3)",
                    colorOuter: "rgba(206, 87, 0, 0)",
                    phase: Math.random() * Math.PI * 2,
                    speed: 0.0008 + Math.random() * 0.0006,
                    parallaxFactor: 0.02,
                });
            }

            // 2. Main distinct liquid spheres (resembling the circles in the reference photo)
            const mainCount = 8;
            const radii = [
                Math.min(width, height) * 0.28,
                Math.min(width, height) * 0.22,
                Math.min(width, height) * 0.17,
                Math.min(width, height) * 0.14,
                Math.min(width, height) * 0.12,
                Math.min(width, height) * 0.09,
                Math.min(width, height) * 0.07,
                Math.min(width, height) * 0.05,
            ];

            for (let i = 0; i < mainCount; i++) {
                const r = radii[i] || 100;
                bubbles.push({
                    type: "main",
                    baseX: Math.random() * width,
                    baseY: Math.random() * height,
                    x: 0,
                    y: 0,
                    vx: (Math.random() - 0.5) * 0.35,
                    vy: (Math.random() - 0.5) * 0.35,
                    radius: r,
                    phase: Math.random() * Math.PI * 2,
                    wobbleSpeed: 0.0012 + Math.random() * 0.001,
                    parallaxFactor: 0.03 + (r / width) * 0.06,
                });
            }

            // 3. Tiny crisp oil droplets / micro beads
            const tinyCount = 18;
            for (let i = 0; i < tinyCount; i++) {
                bubbles.push({
                    type: "tiny",
                    baseX: Math.random() * width,
                    baseY: Math.random() * height,
                    x: 0,
                    y: 0,
                    vx: (Math.random() - 0.5) * 0.25,
                    vy: -0.12 - Math.random() * 0.2, // very gentle upward drift
                    radius: 3 + Math.random() * 10,
                    phase: Math.random() * Math.PI * 2,
                    parallaxFactor: 0.05 + Math.random() * 0.03,
                });
            }

            bubbles.forEach((b) => {
                b.x = b.baseX;
                b.y = b.baseY;
            });
        };

        initBubbles();

        const render = () => {
            // Smooth mouse interpolation
            mouse.x += (mouse.targetX - mouse.x) * 0.04;
            mouse.y += (mouse.targetY - mouse.y) * 0.04;

            // 1. Warm studio orange gradient background
            const bgGrad = ctx.createRadialGradient(
                width * 0.45,
                height * 0.35,
                60,
                width * 0.5,
                height * 0.6,
                Math.max(width, height)
            );
            bgGrad.addColorStop(0, "#F57B26");
            bgGrad.addColorStop(0.5, "#EA6D11");
            bgGrad.addColorStop(1, "#CA5200");
            ctx.fillStyle = bgGrad;
            ctx.fillRect(0, 0, width, height);

            bubbles.forEach((b) => {
                b.phase += b.wobbleSpeed || b.speed || 0.002;
                const wanderX = Math.sin(b.phase) * 20;
                const wanderY = Math.cos(b.phase * 0.8) * 16;

                b.baseX += b.vx;
                b.baseY += b.vy;

                const pad = b.radius * 1.5;
                let wrapped = false;
                if (b.baseX < -pad) {
                    b.baseX = width + pad;
                    wrapped = true;
                } else if (b.baseX > width + pad) {
                    b.baseX = -pad;
                    wrapped = true;
                }
                if (b.baseY < -pad) {
                    b.baseY = height + pad;
                    wrapped = true;
                } else if (b.baseY > height + pad) {
                    b.baseY = -pad;
                    wrapped = true;
                }

                const mouseOffsetX = (mouse.x - width / 2) * b.parallaxFactor;
                const mouseOffsetY = (mouse.y - height / 2) * b.parallaxFactor;

                const dx = b.x - mouse.x;
                const dy = b.y - mouse.y;
                const dist = Math.hypot(dx, dy);

                let pushX = 0;
                let pushY = 0;
                if (dist < mouse.radius + b.radius && dist > 0) {
                    const force = (1 - dist / (mouse.radius + b.radius)) * 18;
                    pushX = (dx / dist) * force;
                    pushY = (dy / dist) * force;
                }

                const targetX = b.baseX + wanderX + mouseOffsetX + pushX;
                const targetY = b.baseY + wanderY + mouseOffsetY + pushY;

                if (wrapped) {
                    // Instantly snap coordinates to avoid shooting across the screen
                    b.x = targetX;
                    b.y = targetY;
                } else {
                    b.x += (targetX - b.x) * 0.05;
                    b.y += (targetY - b.y) * 0.05;
                }

                if (b.type === "ambient") {
                    const grad = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, b.radius);
                    grad.addColorStop(0, b.colorInner);
                    grad.addColorStop(0.5, b.colorMid);
                    grad.addColorStop(1, b.colorOuter);

                    ctx.save();
                    ctx.fillStyle = grad;
                    ctx.beginPath();
                    ctx.arc(b.x, b.y, b.radius, 0, Math.PI * 2);
                    ctx.fill();
                    ctx.restore();
                } else if (b.type === "main") {
                    ctx.save();

                    // 1. Translucent liquid body (more visible & richer)
                    const grad = ctx.createRadialGradient(
                        b.x - b.radius * 0.25,
                        b.y - b.radius * 0.25,
                        b.radius * 0.05,
                        b.x,
                        b.y,
                        b.radius
                    );
                    grad.addColorStop(0, "rgba(255, 195, 110, 0.75)");
                    grad.addColorStop(0.4, "rgba(234, 109, 17, 0.45)");
                    grad.addColorStop(0.8, "rgba(195, 75, 0, 0.6)");
                    grad.addColorStop(1, "rgba(145, 45, 0, 0.85)");

                    ctx.fillStyle = grad;
                    ctx.beginPath();
                    ctx.arc(b.x, b.y, b.radius, 0, Math.PI * 2);
                    ctx.fill();

                    // 2. Liquid meniscus rim / edge outline (bold, glassy refraction)
                    ctx.lineWidth = 2.5;
                    const rimGrad = ctx.createLinearGradient(
                        b.x - b.radius,
                        b.y - b.radius,
                        b.x + b.radius,
                        b.y + b.radius
                    );
                    rimGrad.addColorStop(0, "rgba(255, 245, 220, 0.9)");
                    rimGrad.addColorStop(0.35, "rgba(255, 185, 95, 0.65)");
                    rimGrad.addColorStop(0.7, "rgba(150, 45, 0, 0.45)");
                    rimGrad.addColorStop(1, "rgba(255, 230, 180, 0.8)");

                    ctx.strokeStyle = rimGrad;
                    ctx.stroke();

                    // 3. Highlight reflection (crisp white arc)
                    const hlX = b.x - b.radius * 0.35;
                    const hlY = b.y - b.radius * 0.35;
                    const hlGrad = ctx.createRadialGradient(hlX, hlY, 0, hlX, hlY, b.radius * 0.45);
                    hlGrad.addColorStop(0, "rgba(255, 255, 255, 0.75)");
                    hlGrad.addColorStop(0.35, "rgba(255, 225, 170, 0.4)");
                    hlGrad.addColorStop(1, "rgba(255, 255, 255, 0)");

                    ctx.fillStyle = hlGrad;
                    ctx.beginPath();
                    ctx.arc(hlX, hlY, b.radius * 0.45, 0, Math.PI * 2);
                    ctx.fill();

                    // 4. Secondary lower crescent reflection
                    const cX = b.x + b.radius * 0.25;
                    const cY = b.y + b.radius * 0.3;
                    const cGrad = ctx.createRadialGradient(cX, cY, 0, cX, cY, b.radius * 0.55);
                    cGrad.addColorStop(0, "rgba(255, 210, 130, 0.5)");
                    cGrad.addColorStop(1, "rgba(255, 210, 130, 0)");

                    ctx.fillStyle = cGrad;
                    ctx.beginPath();
                    ctx.arc(cX, cY, b.radius * 0.55, 0, Math.PI * 2);
                    ctx.fill();

                    ctx.restore();
                } else if (b.type === "tiny") {
                    ctx.save();
                    const grad = ctx.createRadialGradient(
                        b.x - b.radius * 0.3,
                        b.y - b.radius * 0.3,
                        0,
                        b.x,
                        b.y,
                        b.radius
                    );
                    grad.addColorStop(0, "rgba(255, 255, 255, 0.95)");
                    grad.addColorStop(0.5, "rgba(255, 190, 100, 0.7)");
                    grad.addColorStop(1, "rgba(180, 50, 0, 0.6)");

                    ctx.fillStyle = grad;
                    ctx.beginPath();
                    ctx.arc(b.x, b.y, b.radius, 0, Math.PI * 2);
                    ctx.fill();

                    ctx.lineWidth = 1.2;
                    ctx.strokeStyle = "rgba(255, 255, 255, 0.85)";
                    ctx.stroke();
                    ctx.restore();
                }
            });

            animationFrameId = requestAnimationFrame(render);
        };

        render();

        return () => {
            window.removeEventListener("resize", handleResize);
            window.removeEventListener("mousemove", handleMouseMove);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="fixed inset-0 pointer-events-none z-0 w-full h-full"
            style={{ display: "block" }}
        />
    );
};
